import React, { type ReactNode } from 'react';
import {
  StyleSheet,
  View,
  type ImageSourcePropType,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import {
  isNative,
  useMediaQueries,
  type WithResponsive,
} from '../utils/responsivity';
import { HeaderTitle } from './HeaderTitle';

type Props = {
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  headerTitle?: ReactNode;
  style?: ViewStyle;
  titleStyle?: ViewStyle;
  desktopLogo?: ImageSourcePropType;
  mobileLogo?: ImageSourcePropType;
  showShadow?: boolean;
};

export const GET_HEADER_HEIGHT = (isMobile: boolean) => (isMobile ? 54 : 76);

export function Header({
  headerLeft,
  headerRight,
  headerTitle,
  titleStyle,
  style,
  desktopLogo,
  mobileLogo,
  showShadow = false,
}: Props) {
  const insets = useSafeAreaInsets();
  const { isMobile } = useMediaQueries();
  const headerHeight = GET_HEADER_HEIGHT(isMobile);
  const headerPadding = isMobile ? 12 : 16;
  const totalHeight = headerHeight + insets.top;
  const styles = useDynamicStyles(createStyles, {
    headerHeight,
    totalHeight,
    headerPadding,
    showShadow,
  });

  const logo = isMobile ? mobileLogo : desktopLogo || mobileLogo;

  return (
    <View style={[styles.container, style]}>
      <>
        <View>
          <View style={[styles.headerTitle, titleStyle]}>
            {headerTitle || (logo && <HeaderTitle logo={logo} />)}
          </View>
        </View>
        <View style={styles.headerLeft}>{headerLeft}</View>
        <View style={styles.headerRight}>{headerRight}</View>
      </>
    </View>
  );
}

const createStyles = ({
  headerHeight,
  totalHeight,
  headerPadding,
  showShadow,
}: WithResponsive<{
  headerHeight: number;
  headerPadding: number;
  totalHeight: number;
  showShadow: boolean;
}>) => {
  // Calculate safe area offset (totalHeight - headerHeight = insets.top)
  const safeAreaTop = (totalHeight ?? 0) - (headerHeight ?? 0);

  return StyleSheet.create({
    headerLeft: {
      position: 'absolute',
      left: 0,
      top: safeAreaTop,
      bottom: 0,
      marginLeft: headerPadding,
      justifyContent: 'center',
    },
    headerRight: {
      position: 'absolute',
      right: 0,
      top: safeAreaTop,
      bottom: 0,
      marginRight: headerPadding,
      justifyContent: 'center',
    },
    headerTitle: {
      height: headerHeight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    container: {
      display: 'flex',
      justifyContent: 'flex-end',
      backgroundColor: Colors.purple0,
      height: totalHeight || 0,
      ...(showShadow && {
        shadowColor: Colors.yellow0,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 1,
        shadowRadius: isNative ? 5 : 10,
        elevation: 10, // For Android shadow
      }),
    },
    twoLineContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
    },
    twoLineControls: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      paddingHorizontal: headerPadding,
      marginTop: 8,
      marginBottom: headerPadding,
    },
    twoLineLeft: {
      marginRight: 16,
    },
    twoLineRight: {
      marginLeft: 16,
    },
    additionalRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      paddingHorizontal: headerPadding,
      marginTop: 8,
      marginBottom: headerPadding,
    },
  });
};
